import { useEffect, useState, useMemo } from 'react';
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription,
} from '../ui/sheet';
import { Button } from '../ui/button';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../ui/tabs';
import { Loader2, ChevronDown, ChevronUp } from 'lucide-react';
import { recordsApi } from '../../lib/api';
import { useBackGestureDismiss } from '../../hooks/useBackGestureDismiss';
import { logActivity } from '../../lib/activityLog';
import { toast } from 'sonner';
import { SupportHint } from './SupportHint';
import { RadarMetaView, MarkLegend } from './RadarMetaView';
import { RadarFindingsList } from './RadarFindingsList';
import { SourceView } from './SourceView';
import { eyebrowDate } from './RecordCard';
import { READING_BODY_CLASS } from './RadarMetaView';
import { RecordHighlights } from '../notes/RecordHighlights';
import { SaveToCollection } from '../notes/SaveToCollection';
import { aboutThem } from '../../lib/findings';

const STAGE_LABEL = {
  segmentation: 'Segmentation',
  record_matching: 'Record matching',
  text_extraction: 'Text extraction',
  radar: 'Copy comparison',
};

const SectionLabel = ({ children }) => (
  <p className="pd-eyebrow">{children}</p>
);

const Chip = ({ children, tone = 'neutral' }) => (
  <span
    className={`inline-flex items-center rounded-full px-[11px] py-1 text-[13px] font-medium leading-tight ${
      tone === 'brand' ? 'bg-brand-wash text-brand-ink' : tone === 'needs' ? 'bg-amber-wash text-amber-ink' : 'bg-inset text-ink-70'
    }`}
  >
    {children}
  </span>
);

/**
 * Full detail for a My Life record. Decrypts on open (server-side) so the
 * list view stays lightweight.
 */
const FRAMING_LABELS = {
  discrediting: 'Your account questioned', reframing: 'Explained away or turned back on you',
  minimising: 'Made to sound smaller', euphemism: 'Softer words for something serious',
  victim_blaming: 'Blame put on you', labelling: 'A label instead of a description',
  adult_centred: 'The adult’s feelings put first', unsupported_conclusion: '“No concerns” beside a concern',
  retraction_context: 'Withdrawn in circumstances worth looking at',
};
const CONCERN_LABELS = {
  neglect: 'Neglect', physical: 'Physical harm', emotional: 'Emotional harm', sexual: 'Sexual harm',
  exploitation: 'Exploitation', domestic_abuse: 'Domestic abuse', self_harm: 'Self-harm', missing: 'Going missing',
  restraint_or_isolation: 'Restraint or being isolated', other: 'A concern',
};
const SOURCE_LABELS = {
  carer: 'a carer', residential_staff: 'staff where you lived', family: 'family', peer: 'another young person',
  professional: 'a professional', self: 'yourself',
};

// Only what's about the person themselves — a passage about another child
// in the same incident isn't "blame put on you".
const noticedCountOf = (d) => { const t = aboutThem(d); return t.framing.length + t.concerns.length; };

// Flagged passages from this record's detection payload: framing first
// (how you were written about), then concerns. Exact quotes only.
const NoticedList = ({ detection: raw }) => {
  const detection = aboutThem(raw);
  return (
  <ul className="space-y-3">
    {(detection?.framing || []).map((f, i) => (
      <li key={`f${i}`} className="rounded-lg bg-care-wash p-3 text-[15px]" data-testid={`noticed-framing-${i}`}>
        <span className="block text-sm font-semibold text-care-ink">{FRAMING_LABELS[f.category] || 'Worth a second look'}</span>
        {f.responds_to && <span className="block text-ink-55">You said: “{f.responds_to}”</span>}
        <span className="block text-ink">“{f.quote}”</span>
        {f.note && <span className="block text-sm text-ink-55">{f.note}</span>}
      </li>
    ))}
    {(detection?.concerns || []).map((c, i) => (
      <li key={`c${i}`} className="rounded-lg bg-ground p-3 text-[15px]" data-testid={`noticed-concern-${i}`}>
        <span className="block text-sm font-semibold text-ink">
          {CONCERN_LABELS[c.category] || 'A concern'}
          {SOURCE_LABELS[c.source_of_harm] ? ` — involving ${SOURCE_LABELS[c.source_of_harm]}` : ''}
          {c.is_allegation ? ' · an allegation' : c.is_disclosure ? ' · something you disclosed' : ''}
        </span>
        {c.quote && <span className="block text-ink">“{c.quote}”</span>}
        {c.child_stated && <span className="block text-sm text-ink-55">Your own words or feelings are recorded here.</span>}
      </li>
    ))}
  </ul>
  );
};

export const RecordDetail = ({ recordId, open, onOpenChange, onOpenDocument, onClose, onRunRadar }) => {
  const [loading, setLoading] = useState(true);
  const [record, setRecord] = useState(null);
  const [activeTab, setActiveTab] = useState('your-record');
  const [analysisOpen, setAnalysisOpen] = useState(false);
  const [openedAt, setOpenedAt] = useState(null);
  // With copies compared, "Your record" shows the comparison marks; the
  // person switches to plain text to highlight.
  const [plainView, setPlainView] = useState(false);

  useEffect(() => {
    if (!open || !recordId) return;
    let cancelled = false;
    setLoading(true);
    setRecord(null);
    setActiveTab('your-record');
    setPlainView(false);
    setOpenedAt(Date.now());
    recordsApi.get(recordId)
      .then((res) => { if (!cancelled) setRecord(res.data); })
      .catch((e) => {
        toast.error(e?.response?.data?.detail || 'Failed to load record');
        onOpenChange(false);
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [open, recordId]); // eslint-disable-line

  const lowConfidence = record?.confidence != null && record.confidence < 0.75;
  const sources = useMemo(() => record?.sources || [], [record?.sources]);
  const radar = record?.radar || null;
  const radarComplete = radar && radar.status === 'complete';
  const noticedCount = noticedCountOf(record?.detection);
  const radarPending = record?.radar_state === 'pending';

  // Fallback "Your record" content when no RADaR yet
  const fallbackText = useMemo(() => {
    if (radarComplete) return null;
    // Prefer the source text from the source matching primary_source_org if known
    if (sources.length > 0) return sources[0]?.full_text || record?.full_text || '';
    return record?.full_text || '';
  }, [radarComplete, sources, record?.full_text]);

  // The text highlights are anchored to: what "Your record" shows, without
  // the comparison tokens.
  const highlightText = radarComplete
    ? (radar?.meta_text || '').replace(/\[\[\/?[A-Z]+[^\]]*\]\]/g, '')
    : fallbackText;

  const copyYourRecord = () => {
    const text = radarComplete
      ? (radar?.meta_text || '').replace(/\[\[\/?[A-Z]+[^\]]*\]\]/g, '')
      : fallbackText;
    if (!text) return;
    navigator.clipboard.writeText(text);
    toast.success('Copied');
  };

  const dateText = record ? eyebrowDate(record.date || record.approximate_date, record.date_human) : '';

  const handleClose = (v) => {
    if (!v && record) {
      const duration = openedAt ? Date.now() - openedAt : 0;
      const bracket = duration < 60_000
        ? 'under_1_min'
        : duration <= 300_000
        ? '1_to_5_min'
        : 'over_5_min';
      logActivity('record_read_duration', { duration: bracket });
      onClose?.({
        durationMs: duration,
        sensitive: !!record.sensitive,
        orgs: record.support_orgs || [],
      });
    }
    onOpenChange(v);
  };

  // On mobile this sheet is full screen, so the OS/browser back gesture
  // should close it rather than navigate away from the page it was opened
  // from — see useBackGestureDismiss.
  const handleOpenChange = useBackGestureDismiss(open, handleClose);

  return (
    <Sheet open={open} onOpenChange={handleOpenChange}>
      <SheetContent
        side="right"
        className="w-full sm:max-w-3xl overflow-y-auto p-6 sm:p-9"
        data-testid="record-detail-sheet"
      >
        <SheetHeader className="space-y-4 text-left pr-8">
          {loading ? (
            <div className="flex items-center gap-2 text-ink-55 py-6">
              <Loader2 className="w-4 h-4 animate-spin" aria-hidden="true" /> Unlocking this record…
            </div>
          ) : record ? (
            <>
              <div className="space-y-2">
                <SheetDescription className="pd-mono text-ink-55">
                  {record.date_approximate && <span className="normal-case">c. </span>}
                  {dateText}
                  <span aria-hidden="true"> · </span>
                  {record.doc_type || 'Record'}
                </SheetDescription>
                <SheetTitle className="font-heading text-[24px] sm:text-[28px] font-semibold tracking-[-0.02em] leading-[1.2] text-ink">
                  {record.assembled_label}
                </SheetTitle>
                <div className="flex flex-wrap items-center gap-2 pt-1">
                  {sources.length > 1 && <Chip>{sources.length} sources</Chip>}
                  {radarComplete && <Chip tone="brand">Copies compared</Chip>}
                  <SaveToCollection targetType="record" targetId={record.id} testId="record-save-to-collection" />
                </div>
              </div>

              {lowConfidence && (
                <p
                  className="rounded-lg bg-amber-wash text-amber-ink px-4 py-3 text-[15px] leading-relaxed"
                  data-testid="record-low-confidence-banner"
                >
                  The details of this record were read with low confidence. Check them against the source document before you rely on them.
                </p>
              )}

              {radarPending && sources.length >= 2 && (
                <div
                  className="rounded-lg bg-ground px-4 py-3 flex flex-col sm:flex-row sm:items-center gap-3"
                  data-testid="record-radar-pending"
                >
                  <p className="flex-1 text-[15px] leading-relaxed text-ink-70">
                    {sources.length} organisations sent a copy of this record. Comparing them can recover text that one copy blacked out.
                  </p>
                  {onRunRadar && (
                    <Button size="sm" variant="secondary" className="bg-surface hover:bg-inset" onClick={onRunRadar} data-testid="run-radar-from-detail">
                      Compare copies
                    </Button>
                  )}
                </div>
              )}
            </>
          ) : null}
        </SheetHeader>

        {record && !loading && (
          <div className="mt-7 space-y-8">
            <SupportHint record={record} />

            {/* Tabbed content: Your Record / Source N / RADaR findings */}
            <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-5">
              <TabsList className="flex-wrap h-auto justify-start">
                <TabsTrigger value="your-record" data-testid="tab-your-record">
                  Your record
                </TabsTrigger>
                {sources.map((s, i) => (
                  <TabsTrigger
                    key={s.document_id}
                    value={`source-${i}`}
                    data-testid={`tab-source-${i}`}
                  >
                    {s.organisation_name || `Copy ${i + 1}`}
                  </TabsTrigger>
                ))}
                {noticedCount > 0 && (
                  <TabsTrigger value="noticed" data-testid="tab-noticed">
                    What we noticed ({noticedCount})
                  </TabsTrigger>
                )}
                {radarComplete && (radar.findings?.length ?? 0) > 0 && (
                  <TabsTrigger value="findings" data-testid="tab-findings">
                    Comparison findings ({radar.findings.length})
                  </TabsTrigger>
                )}
              </TabsList>

              {/* Your record tab */}
              <TabsContent value="your-record" className="space-y-4">
                <div className="flex items-center justify-between gap-3">
                  <SectionLabel>
                    {radarComplete ? 'Reconstructed from every copy' : 'Text as it appears on the page'}
                  </SectionLabel>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={copyYourRecord}
                    data-testid="record-copy-text"
                  >
                    Copy text
                  </Button>
                </div>

                {radarComplete && (
                  <div className="inline-flex rounded-md bg-ground p-1" role="tablist" aria-label="How to show this record">
                    {[[false, 'Comparison marks'], [true, 'Plain text to highlight']].map(([v, label]) => (
                      <button
                        key={label}
                        type="button"
                        role="tab"
                        aria-selected={plainView === v}
                        onClick={() => setPlainView(v)}
                        className={`rounded px-3 py-1.5 text-sm font-medium ${plainView === v ? 'bg-surface text-ink shadow-sm' : 'text-ink-55 hover:text-ink'}`}
                        data-testid={`record-view-${v ? 'plain' : 'marks'}`}
                      >
                        {label}
                      </button>
                    ))}
                  </div>
                )}

                <RecordHighlights
                  recordId={record.id}
                  text={highlightText}
                  className={READING_BODY_CLASS}
                  readOnlyView={radarComplete && !plainView ? (
                    <>
                      <p className="text-sm text-ink-55">Switch to “Plain text to highlight” to mark passages.</p>
                      <RadarMetaView metaText={radar.meta_text} summary={radar.summary} />
                    </>
                  ) : null}
                />

                {radar?.status === 'failed' && (
                  <p className="rounded-lg bg-breach-wash text-breach-ink px-4 py-3 text-[15px] leading-relaxed">
                    Comparing the copies did not finish last time{radar.error ? `: ${radar.error}` : ''}. You can try again from the My life page.
                  </p>
                )}

                <MarkLegend />
              </TabsContent>

              {noticedCount > 0 && (
                <TabsContent value="noticed" className="space-y-4" data-testid="record-noticed">
                  <p className="text-sm text-ink-55">
                    An automated reading of this record, in its own words. A phrase can read worse than it was meant,
                    and this can be wrong — it’s here so you can look for yourself.
                  </p>
                  <NoticedList detection={record.detection} />
                </TabsContent>
              )}

              {/* Source tabs */}
              {sources.map((s, i) => (
                <TabsContent key={s.document_id} value={`source-${i}`} className="space-y-4">
                  <div className="flex items-center gap-2 flex-wrap">
                    <SectionLabel>From {s.organisation_name || `copy ${i + 1}`}</SectionLabel>
                    {s.has_redactions && (
                      <Chip>
                        {s.redaction_count ? `${s.redaction_count} redaction${s.redaction_count === 1 ? '' : 's'}` : 'Redactions'}
                      </Chip>
                    )}
                    {s.redacted_pages > 0 && (
                      <Chip tone="needs">
                        {s.redacted_pages} whole page{s.redacted_pages === 1 ? '' : 's'} blacked out
                      </Chip>
                    )}
                    {s.redacted_sections > 0 && (
                      <Chip tone="needs">
                        {s.redacted_sections} section{s.redacted_sections === 1 ? '' : 's'} blacked out
                      </Chip>
                    )}
                    {s.illegible_count > 0 && (
                      <Chip>
                        {s.illegible_count} part{s.illegible_count === 1 ? '' : 's'} couldn’t be read
                      </Chip>
                    )}
                    {radar?.primary_source_org && s.organisation_name === radar.primary_source_org && (
                      <Chip tone="brand">Main copy</Chip>
                    )}
                    <Button
                      size="sm"
                      variant="ghost"
                      className="ml-auto"
                      onClick={() => onOpenDocument?.(s.document_id, s.page_range?.[0])}
                      data-testid={`open-source-doc-${i}`}
                    >
                      Open the document
                    </Button>
                  </div>
                  <SourceView source={s} />
                  <MarkLegend />
                </TabsContent>
              ))}

              {/* RADaR findings tab */}
              {radarComplete && (radar.findings?.length ?? 0) > 0 && (
                <TabsContent value="findings" className="space-y-4">
                  <p className="text-[15px] text-ink-55 leading-relaxed max-w-[68ch]">
                    Each finding checks one blacked-out passage or gap in the main copy against the other copies, says what was probably removed, and whether you could challenge it.
                  </p>
                  <RadarFindingsList findings={radar.findings} />
                </TabsContent>
              )}
            </Tabs>

            {/* Key people */}
            {record.people?.length > 0 && (
              <section className="space-y-2">
                <SectionLabel>People named</SectionLabel>
                <ul className="divide-y divide-rule">
                  {record.people.map((p, idx) => (
                    <li key={`p-${idx}`} className="flex items-baseline gap-2 py-2 text-[16px]">
                      <span className="font-medium text-ink">{p.name}</span>
                      {p.role && <span className="text-ink-55">· {p.role}</span>}
                    </li>
                  ))}
                </ul>
              </section>
            )}

            {/* Places */}
            {record.places?.length > 0 && (
              <section className="space-y-2">
                <SectionLabel>Places</SectionLabel>
                <ul className="divide-y divide-rule">
                  {record.places.map((p, idx) => (
                    <li key={`pl-${idx}`} className="flex items-baseline gap-2 py-2 text-[16px]">
                      <span className="text-ink">{p.name}</span>
                      {p.type && <span className="text-ink-55">· {p.type}</span>}
                    </li>
                  ))}
                </ul>
              </section>
            )}

            {/* Reference numbers */}
            {(record.identifiers?.length > 0 || record.reference_numbers?.length > 0) && (
              <section className="space-y-2" data-testid="record-identifiers">
                <SectionLabel>Reference numbers</SectionLabel>
                <div className="flex flex-wrap gap-2">
                  {(record.identifiers?.length ? record.identifiers.map((x) => x.text) : record.reference_numbers).map((r, i) => (
                    <span key={`r-${i}`} className="pd-mono normal-case rounded-md bg-inset text-ink px-3 py-1.5 text-[13px]">
                      {r}
                    </span>
                  ))}
                </div>
              </section>
            )}

            {/* Library tags */}
            {record.library_tags?.length > 0 && (
              <section className="space-y-2">
                <SectionLabel>Themes</SectionLabel>
                <div className="flex flex-wrap gap-2">
                  {record.library_tags.map((t) => (
                    <Chip key={t}>{record.library_tags_display?.[t] || t}</Chip>
                  ))}
                </div>
              </section>
            )}

            {/* Analysis detail (collapsible) */}
            <section className="border-t border-rule pt-5">
              <button
                type="button"
                onClick={() => setAnalysisOpen((v) => !v)}
                className="flex items-center gap-2 text-[15px] font-medium text-ink-55 hover:text-ink rounded-md"
                aria-expanded={analysisOpen}
                data-testid="record-analysis-toggle"
              >
                {analysisOpen ? <ChevronUp className="w-4 h-4 stroke-[1.5]" aria-hidden="true" /> : <ChevronDown className="w-4 h-4 stroke-[1.5]" aria-hidden="true" />}
                How this record was read
              </button>
              {analysisOpen && (
                <dl className="mt-4 grid grid-cols-[auto_1fr] gap-x-6 gap-y-2 text-[15px] text-ink-70">
                  <dt className="font-medium text-ink">Pages</dt>
                  <dd className="pd-mono normal-case text-ink">
                    {record.page_range ? `${record.page_range[0]}–${record.page_range[1]}` : 'unknown'}
                  </dd>
                  <dt className="font-medium text-ink">Pipeline version</dt>
                  <dd>v{record.pipeline_version ?? '?'}</dd>
                  <dt className="font-medium text-ink">Confidence</dt>
                  <dd>{record.confidence != null ? `${Math.round(record.confidence * 100)}%` : '—'}</dd>
                  <dt className="font-medium text-ink self-start">Process runs</dt>
                  <dd>
                    {(record.process_runs || []).length === 0 ? (
                      <span className="text-ink-55">None recorded for the source document.</span>
                    ) : (
                      <ul className="space-y-1">
                        {record.process_runs.map((r) => (
                          <li key={r.id} className="flex items-center gap-3">
                            <span className="pd-mono normal-case text-ink-55">v{r.prompt_version}</span>
                            <span>{STAGE_LABEL[r.process_id] || r.process_id}</span>
                            <span className="ml-auto">
                              {r.confidence != null ? `${Math.round(r.confidence * 100)}%` : '—'}
                              {r.flagged && <span className="text-amber-ink ml-2">flagged</span>}
                            </span>
                          </li>
                        ))}
                      </ul>
                    )}
                  </dd>
                </dl>
              )}
            </section>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
};

export default RecordDetail;
